import { fragmentEncounter } from '@ctw/shared/api/fqs/queries/fragments/encounter';
import { gql } from 'graphql-request';

/**
 * {@link fragmentEpisodeOfCareOverview} minus `status`, for a consumer whose selection
 * set already has a differently-typed `status` sibling (e.g. Encounter's) and needs to
 * alias `status` itself.
 */
export const fragmentEpisodeOfCareOverviewFields = gql`
  fragment EpisodeOfCareOverviewFields on EpisodeOfCare {
    id
    resourceType
    extension {
      url
      extension {
        url
        valueReference {
          id
          type
          reference
          display
          extension {
            url
            valueString
            valueBoolean
          }
        }
        valueString
        valueCodeableConcept {
          coding {
            system
            code
            display
          }
          text
        }
      }
      valueString
      valueReference {
        id
        type
        reference
        display
      }
      valueCodeableConcept {
        coding {
          system
          code
          display
        }
        text
      }
      valueCoding {
        code
        display
        system
        userSelected
        version
      }
    }
    meta {
      extension {
        url
      }
      security {
        system
        code
      }
      tag {
        system
        code
      }
      lastUpdated
    }
    period {
      start
      end
    }
  }
`;

/**
 * Overview-only fragment for EpisodeOfCare. Same shape as
 * {@link fragmentEpisodeOfCareWithoutPatient} but omits the nested
 * `extension.extension.valueReference.resource ... on Encounter` block, which is
 * the expensive part (pulls ProvenanceList + full Condition/Practitioner/Location
 * resources per encounter). Use this when the consumer only needs EoC-level
 * extension data (title, period, class, location labels, discharge binary refs,
 * nonelective/readmission/behavioral flags).
 *
 * Trade-off: models fetched via this fragment will return an empty
 * `aggregatedEncounters` array because the inner Encounter resource is omitted.
 */
export const fragmentEpisodeOfCareOverview = gql`
  ${fragmentEpisodeOfCareOverviewFields}
  fragment EpisodeOfCareOverview on EpisodeOfCare {
    ...EpisodeOfCareOverviewFields
    status
  }
`;

export const fragmentEpisodeOfCareWithoutPatient = gql`
  ${fragmentEncounter}
  fragment EpisodeOfCare on EpisodeOfCare {
    id
    resourceType
    extension {
      url
      extension {
        url
        valueReference {
          id
          type
          reference
          display
          extension {
            url
            valueString
            valueBoolean
          }
            resource {
                ... on Encounter {
                    ...Encounter
                }
            }
        }
        valueString
      }
      valueString
      valueReference {
        id
        type
        reference
        display
      }
      valueCodeableConcept {
        coding {
          system
          code
          display
        }
        text
      }
      valueCoding {
        code
        display
        system
        userSelected
        version
      }
    }
    meta {
      extension {
        url
      }
      security {
        system
        code
      }
      tag {
        system
        code
      }
      lastUpdated
    }
    period {
      start
      end
    }
    identifier {
      system
      value
      type {
        text
        coding {
          system
          code
          display
        }
      }
    }
    status
    diagnosis {
      role {
        coding {
          system
          code
          display
        }
      }
      condition {
        id
        type
        reference
        resource {
          ... on Condition {
            id
            resourceType
            code {
              text
              coding {
                display
                code
                system
                extension {
                  url
                  valueString
                }
              }
            }
          }
        }
      }
    }
  }
`;
